import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatError, MatFormField, MatHint, MatLabel, MatPrefix, MatSuffix } from '@angular/material/select';
import { EnpsControlComponent } from '@src/suite/common/controls/enps-control/enps-control';
import { INITIAL_TIME_STAMP } from '@src/suite/common/enums/constants';
import { Duration } from 'luxon';

import { isValidDurationSecondsFormat, secondsToDuration, validateValue } from '../../utils/duration-utils';

@Component({
    selector: 'st-duration-seconds-input-field',
    imports: [FormsModule, MatError, MatFormField, MatHint, MatInput, MatLabel, MatPrefix, MatSuffix],
    templateUrl: './duration-seconds-input-field.html',
    styleUrl: './duration-seconds-input-field.scss',
    // TODO This might need some manual change detection - check pls
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-duration-seconds-input-field',
    },
})
export class DurationSecondsInputFieldComponent extends EnpsControlComponent<string> {
    readonly enter = output<string>();

    readonly type = input<'text'>('text');
    readonly showLinks = input(false, {
        transform: coerceBooleanProperty,
    });
    readonly textToHighlight = input<string>(null);

    readonly inEditMode = signal(true);
    readonly internalValue = linkedSignal<string, string>({
        source: this.value,
        computation: (raw: string | Duration) => {
            if (!raw) return INITIAL_TIME_STAMP;
            let durationValue = Duration.isDuration(raw) ? raw.as('seconds') : raw;

            if (String(durationValue).includes('.')) {
                durationValue = Math.round(parseFloat(String(durationValue)));
            }

            // Check if the duration value is valid (only digits) before converting it to "hh:mm:ss" format
            const isValid = isValidDurationSecondsFormat(durationValue);
            if (!isValid) {
                return validateValue(durationValue);
            }

            // secondsToDuration takes a duration value in seconds and converts it to a string in the format "hh:mm:ss". For example, if the input is 3661, it will return "01:01:01".
            // validateValue takes a duration string in the format "hh:mm:ss" and checks if it is valid. It ensures that the hours, minutes, and seconds are within the appropriate ranges (e.g., hours should be between 0 and 59, minutes and seconds should be between 0 and 59). If the input is not valid, it will return a default value (INITIAL_TIME_STAMP).
            return validateValue(secondsToDuration(durationValue));
        },
    });

    readonly displayValue = computed(() => {
        return (this.value() as string) || this.placeholder() || null;
    });

    keyPressed(event) {
        if (event.keyCode === 27) {
            this.elmRef.nativeElement.querySelector('input').blur();
            this.inEditMode.set(false);
        }

        if (event.keyCode === 13 || event.key === 'Enter') {
            this.onValueChanged();
            this.enter.emit(this.internalValue());
        }
    }

    onNgModelChanged(value: string) {
        this.internalValue.set(value);
    }

    onValueChanged() {
        if (this.internalValue() && isValidDurationSecondsFormat(this.internalValue())) {
            const value = secondsToDuration(this.internalValue());
            this.internalValue.set(validateValue(value));
        } else {
            this.internalValue.set(validateValue(this.internalValue()));
        }

        this.changed.emit(this.internalValue());

        if (this.showLinks && !this.disabled()) {
            this.inEditMode.set(false);
        }
    }

    onBlur() {
        if (this.showLinks && !this.disabled()) {
            this.inEditMode.set(false);
        }
    }

    onFocus() {
        if (this.showLinks && !this.disabled()) {
            this.inEditMode.set(true);
        }
    }
}
